import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { blockService } from '../services';
import { useDialog } from '../context';
import { A, Avatar, AVATAR_TINTS, Card, CardTitle, Pill, Prose } from './account/kit';

/**
 * "Blocked people" in Settings (Sept 2026 redesign: one white card).
 *
 * Only ever lists people YOU blocked. The API deliberately never returns who
 * blocked you — being told would defeat the purpose of the block.
 */
export default function BlockedUsersSection({ pad }) {
  const { showConfirm, showAlert } = useDialog();
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const r = await blockService.list();
    if (r.success) setBlocked(r.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const unblock = async (person) => {
    const name = person.displayName || person.handle || 'this person';
    const ok = await showConfirm({
      title: 'UNBLOCK?',
      message: `${name} will be able to find you, message you, and see you on guest lists again.`,
      confirmLabel: 'Unblock',
      cancelLabel: 'Keep blocked',
    });
    if (!ok) return;

    setBusyId(person.userId);
    const r = await blockService.unblock(person.userId);
    setBusyId(null);
    if (r.success) setBlocked((prev) => prev.filter((b) => b.userId !== person.userId));
    else showAlert({ title: 'ERROR', message: r.error || 'Could not unblock.', type: 'error' });
  };

  return (
    <Card pad={pad} gap={14}>
      <CardTitle>BLOCKED PEOPLE</CardTitle>
      <Prose>Blocked people can’t find you in search, message you, or see you on a guest list — and you won’t see them either.</Prose>

      {loading ? (
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: A.grey400 }}>LOADING…</span>
      ) : blocked.length === 0 ? (
        <span style={{ alignSelf: 'flex-start', padding: '10px 14px', borderRadius: 16, background: A.canvas, fontSize: 12, color: A.grey500 }}>You haven’t blocked anyone.</span>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {blocked.map((person, i) => (
            <li key={person.userId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderRadius: 16, background: A.canvas }}>
              <div style={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 12 }}>
                <Avatar src={person.avatarUrl} name={person.displayName || person.handle} tint={AVATAR_TINTS[i % AVATAR_TINTS.length]} size={40} radius={12} />
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{person.displayName || person.handle || 'Someone'}</span>
                  {person.handle && <Link to={`/${person.handle}`} style={{ fontSize: 11, color: A.grey500, textDecoration: 'none' }}>@{person.handle}</Link>}
                </div>
              </div>
              <Pill kind="white" onClick={() => unblock(person)} disabled={busyId === person.userId} style={{ height: 32, padding: '0 14px', fontSize: 10 }}>
                {busyId === person.userId ? '…' : 'UNBLOCK'}
              </Pill>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
